/**
 * Teams Hub - Main Application
 * 
 * Real-time team collaboration platform with:
 * - Channels (group discussions, job-linked)
 * - Direct Messages (1:1 conversations)
 * - Notes integration (cross-hub)
 * - People directory with presence
 * 
 * Uses @quotech/ui DashboardLayout for universal navigation.
 * Uses @quotech/auth for SSO and protected routes.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom'
import { 
  DashboardLayout, 
  TabBar,
  type User, 
  getHubBreadcrumbs,
  buildSettingsTabs,
  useGlobalSearchBridge,
  useTabNavigation,
  useTabKeyboardShortcuts,
  ErrorBoundary,
  VersionWatcher,
} from '@quotech/ui'
import { useAuth, UnifiedSSOPage, ProtectedRoute, HubGuard, SessionManager, useComputedIsAdmin, useAuthStore, useRBAC } from '@quotech/auth'
import { useApiClient } from '@quotech/api'
import {
  MODULE_CONFIG,
  NAV_ITEMS,
  SECONDARY_NAV_ITEMS,
  EXTERNAL_LINKS,
  COMMANDS,
  HUB_LINKS,
  QUICK_ADD_ITEMS,
} from './config/navigation'
import { TEAMS_TAB_CONFIG, useTeamsTabStore } from './config/tabs'
import { usersApi } from './lib/api'
import { useTeamsStore } from './store/teamsStore'

// Pages
import DashboardPage from './pages/DashboardPage'
import ChannelsPage from './pages/ChannelsPage'
import ChannelDetailPage from './pages/ChannelDetailPage'
import MessagesPage from './pages/MessagesPage'
import NotesPage from './pages/NotesPage'
import PeoplePage from './pages/PeoplePage'
import AnnouncementsPage from './pages/AnnouncementsPage'
import SettingsPage from './pages/SettingsPage'

/**
 * Main layout wrapper using @quotech/ui
 */
function TeamsLayout() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const { totalUnreadChannels, totalUnreadDMs, setMyStatus } = useTeamsStore()

  // Update presence on mount and interval
  useEffect(() => {
    const updatePresence = async () => {
      try {
        await usersApi.updatePresence('online')
      } catch (e) {
        // Silent fail
      }
    }
    
    updatePresence()
    const interval = setInterval(updatePresence, 60000)
    
    // Set offline on unmount
    return () => {
      clearInterval(interval)
      usersApi.updatePresence('offline').catch(() => { /* silent: best-effort cleanup */ })
    }
  }, [])

  // Build breadcrumbs
  const breadcrumbs = getHubBreadcrumbs('teams', location.pathname)
  
  // Global Search Bridge - provides API client with auth for search to work
  const tokenProvider = useMemo(() => ({
    getAccessToken: () => useAuthStore.getState().accessToken,
    getOrgId: () => useAuthStore.getState().selectedOrg?.id,
  }), [])
  
  const { apiClient: searchApiClient } = useGlobalSearchBridge({
    tokenProvider,
    onSettingsOpen: useCallback(() => setSettingsOpen(true), []),
  })

  // API client for settings tabs
  const apiClient = useApiClient()

  const computedIsAdmin = useComputedIsAdmin(user as Record<string, any> | null)
  const { accessibleModules, permissions: rbacPermissions, hierarchyLevel } = useRBAC()

  // Map auth user to UI user format - includes ALL admin-related fields
  const uiUser: User & { displayEmail?: string } | null = user ? {
    id: user.id,
    name: user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
    email: user.email,
    // Header-only authority display; login identity stays on `email`.
    displayEmail: user.display_email || user.authority_email || user.email,
    organisation: user.organisation_name,
    organisationId: user.organisation_id,
    role: user.role,
    isAdmin: computedIsAdmin,
    admin: (user as any).admin,
    is_admin: (user as any).is_admin,
    is_staff: (user as any).is_staff,
    is_superuser: (user as any).is_superuser,
  } : null

  // Handle logout
  const handleLogout = () => {
    setMyStatus('offline')
    logout()
    window.location.href = 'https://dashboard.quotech.ai/login'
  }

  // Build settings tabs
  const settingsTabs = useMemo(() => buildSettingsTabs({
    user: user ? {
      id: user.id,
      first_name: user.first_name || user.name?.split(' ')[0],
      last_name: user.last_name || user.name?.split(' ').slice(1).join(' '),
      email: user.email,
      organisation_name: user.organisation_name,
      role: user.role,
      isAdmin: computedIsAdmin,
      permissions: rbacPermissions, hierarchy_level: hierarchyLevel,
    } : null,
    hubId: 'teams',
    hubName: 'Teams Hub',
    apiClient,
    organisationId: user?.organisation_id || '',
    profileData: {
      first_name: user?.first_name || user?.name?.split(' ')[0],
      last_name: user?.last_name || user?.name?.split(' ').slice(1).join(' '),
      email: user?.email,
      organisation_name: user?.organisation_name,
    },
    organisationData: {
      id: user?.organisation_id || '',
      name: user?.organisation_name || 'Organisation',
    },
    integrations: [], // Teams Hub doesn't have direct integrations
  }), [user, computedIsAdmin, apiClient, rbacPermissions, hierarchyLevel])
  
  // Tab navigation (browser-style multi-tab)
  const tabNav = useTabNavigation(TEAMS_TAB_CONFIG)
  useTabKeyboardShortcuts({ useStore: useTeamsTabStore, ...tabNav })

  // Add badge counts to nav items
  const navItemsWithBadges = useMemo(() => 
    NAV_ITEMS.map(item => {
      if (item.id === 'channels') return { ...item, badge: totalUnreadChannels }
      if (item.id === 'messages') return { ...item, badge: totalUnreadDMs }
      return item
    }), [totalUnreadChannels, totalUnreadDMs]
  )
  
  // Update SECONDARY_NAV_ITEMS to use action for settings
  const secondaryNavWithSettingsAction = useMemo(() => 
    SECONDARY_NAV_ITEMS.map(item => 
      item.id === 'settings' 
        ? { ...item, href: undefined, action: () => setSettingsOpen(true) }
        : item
    ), []
  )

  return (
    <DashboardLayout
      navItems={navItemsWithBadges}
      secondaryNavItems={secondaryNavWithSettingsAction}
      externalLinks={EXTERNAL_LINKS}
      module={MODULE_CONFIG}
      currentHub="teams"
      accessibleModules={accessibleModules}
      user={uiUser}
      onLogout={handleLogout}
      breadcrumbs={breadcrumbs}
      showGlobalSearch={true}
      globalSearchApiClient={searchApiClient}
      showCommandPalette
      commands={COMMANDS}
      hubLinks={HUB_LINKS}
      showOrgSwitcher
      showHubSwitcher
      showQuickAdd
      quickAddItems={QUICK_ADD_ITEMS}
      showNotifications
      notificationCount={totalUnreadChannels + totalUnreadDMs}
      sidebarSectionLabels={{
        main: 'Teams',
        secondary: 'Tools',
        hubs: 'Other Hubs',
      }}
      contentClassName="px-0 py-0"
      headerTabsSlot={
        <TabBar
          variant="inline"
          tabs={tabNav.tabItems}
          activeTabId={tabNav.activeTabId}
          onTabClick={tabNav.handleTabClick}
          onTabClose={tabNav.handleTabClose}
          onNewTab={tabNav.handleNewTab}
          onTabPin={tabNav.handleTabPin}
          onCloseOthers={tabNav.handleCloseOthers}
          onDuplicateTab={tabNav.handleDuplicateTab}
          accentColor="bg-violet-500"
        />
      }
      showSettingsModal
      settingsTabs={settingsTabs}
      settingsModalOpen={settingsOpen}
      onCloseSettings={() => setSettingsOpen(false)}
      onOpenSettings={() => setSettingsOpen(true)}
    >
      <Outlet />
    </DashboardLayout>
  )
}

/**
 * Main App with unified SSO and session management
 */
function App() {
  // Initialize unified session manager
  useEffect(() => {
    const sessionManager = new SessionManager()
    sessionManager.startMonitoring()
    return () => sessionManager.stopMonitoring()
  }, [])

  return (
    <ErrorBoundary>
    {/* Auto-update notifier — see qf!2578 for context (30s poll, 10-min idle auto-reload). */}
    <VersionWatcher pollIntervalMs={30_000} autoReloadAfterMs={null} />
    <Routes>
      {/* Unified SSO Page */}
      <Route
        path="/sso"
        element={
          <UnifiedSSOPage
            defaultRedirectPath="/dashboard"
            loadingComponent={
              <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                <div className="text-center">
                  <div className="w-10 h-10 border-4 border-violet-600 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Signing you in to Teams Hub...</p>
                </div>
              </div>
            }
          />
        }
      />

      {/* Protected routes with shared layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute redirectTo="https://dashboard.quotech.ai/login">
            <HubGuard hub="teams"><TeamsLayout /></HubGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="channels" element={<ChannelsPage />} />
        <Route path="channels/new" element={<ChannelsPage showCreate />} />
        <Route path="channels/:channelId" element={<ChannelDetailPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="messages/new" element={<MessagesPage showCreate />} />
        <Route path="messages/:channelId" element={<ChannelDetailPage />} />
        <Route path="notes" element={<NotesPage />} />
        <Route path="notes/new" element={<NotesPage showCreate />} />
        <Route path="people" element={<PeoplePage />} />
        <Route path="announcements" element={<AnnouncementsPage />} />
        <Route path="announcements/new" element={<AnnouncementsPage showCreate />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="search" element={<Navigate to="/dashboard" replace />} />
        <Route path="notifications" element={<Navigate to="/dashboard" replace />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </ErrorBoundary>
  )
}

export default App
